import {
  BackgroundColors,
  NegativeColors,
  PositiveColors,
} from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';

import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { BreakPointsMedia } from '@picsart/design-system/foundation/responsive';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { createUseStyles } from 'react-jss';

import { ColorSchemes } from 'redesignedComponents/context/colorScheme/enums';
import type { ColorSchemeType } from 'redesignedComponents/context/colorScheme/types';

type StyleProps = {
  colorScheme?: ColorSchemeType;
};

const useStyles = createUseStyles<string, StyleProps>(
  () => ({
    root: ({ colorScheme }) => ({
      '&&': {
        position: 'relative',
        padding: [Spacings.s64, Spacings.s0],
        color: (props: StyleProps) => (props?.colorScheme === ColorSchemes.LIGHT ? 'red' : 'red'),
        backgroundColor:
          colorScheme === ColorSchemes.LIGHT
            ? PositiveColors.base.default
            : NegativeColors.base.default,
        [BreakPointsMedia.LG]: {
          padding: [Spacings.s40, Spacings.s0],
        },
        [BreakPointsMedia.MD]: {
          padding: [Spacings.s40, Spacings.s0],
        },
        // [mediaQuery(767)]: {
        '@media screen and (max-width: 767px)': {
          padding: [Spacings.s40, Spacings.s0],
        },
      },
    }),
    topContainer: {
      display: 'grid',
      margin: [0, 'auto', Spacings.s24],
      gap: Spacings.s16,
    },
    title: {
      '&&': {
        margin: [Spacings.s0, 'auto'],
        textAlign: 'center',

        '@media screen and (max-width: 766px)': {
          fontSize: FontSizes.s24,
        },
      },
    },
    description: {
      '&&': {
        margin: [Spacings.s24, 'auto'],
        textAlign: 'center',
        fontSize: FontSizes.s20,
        lineHeight: LineHeights.l28,

        '@media screen and (max-width: 766px)': {
          margin: [Spacings.s12, 'auto'],
        },
      },
    },

    cta: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      marginTop: 0,
    },
    carouselWrapper: {
      '@media screen and (max-width: 480px)': {
        paddingRight: 0,
      },
    },
    carouselControl: {
      '&&': {
        alignItems: 'center',
        '&.prev': {
          left: `calc(${Spacings.s40} * -1)`,

          '@media screen and (max-width: 766px)': {
            left: `calc(${Spacings.s8} * -1)`,
          },
        },

        '&.next': {
          right: `calc(${Spacings.s40} * -1)`,

          '@media screen and (max-width: 766px)': {
            right: `calc(${Spacings.s8} * -1)`,
          },
        },
        '& i': {
          width: 34,
          height: 34,
          padding: Spacings.s2,
          background: BackgroundColors.tint1,
          borderRadius: CornerRadius.r16,
          marginTop: `calc(${Spacings.s32} * -1)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0px 0px 16px 0px rgba(0, 0, 0, 0.24)',
          position: 'absolute',
          top: '40%',

          '& svg': {
            width: 16,
            height: 16,
          },
        },

        '& > button': {
          height: '100%',
          '& svg': {
            '& > path': {
              fill: PositiveColors.text.default,
            },
          },
        },
        '&.disabled': {
          visibility: 'hidden',
          opacity: 0,
        },
      },
      '@media screen and (max-width: 480px)': {
        '&&': {
          display: 'none',
        },
      },
    },
    carouselContainer: {
      '&&': {
        overflow: 'visible',
      },
      '@media screen and (max-width: 480px)': {
        '&>div': {
          overflow: 'scroll',
          scrollbarWidth: 'none',
        },
      },
    },

    carousel: {
      margin: [Spacings.s0, 'auto'],
      width: '100%',
      maxWidth: 1076,
      marginBottom: Spacings.s16,

      [BreakPointsMedia.LG]: {
        position: 'initial',
        maxWidth: 934,
      },

      '@media screen and (max-width: 766px)': {
        marginBottom: 'initial',
      },
    },
    // skeleton
    skeletonRoot: {
      extend: 'root',
      width: '100%',
      maxWidth: 1076,
      margin: [Spacings.s0, 'auto'],
    },
    skeletonTitle: {
      minHeight: 32,
      maxWidth: 400,
      margin: [Spacings.s0, 'auto'],
    },

    sekeletonBtn: {
      height: 40,
      width: 200,
      borderRadius: CornerRadius.r20,
      margin: [Spacings.s44, 'auto'],
    },
    sekeletonItemWrapper: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',

      '@media screen and (max-width: 766px)': {
        gridTemplateColumns: '1fr',
        '& > div:not(:first-child)': {
          display: 'none',
        },
      },
    },
    skeletonItem: {
      margin: [Spacings.s24, 'auto', Spacings.s24],
      borderRadius: CornerRadius.r20,
      width: 342,
      height: 234,

      '@media screen and (max-width: 766px)': {
        maxWidth: 218,
        maxHeight: 136,
        minWidth: 148,
      },
    },
    skeletonDots: {
      width: 48,
      display: 'flex',
      margin: [Spacings.s24, 'auto'],
      justifyContent: 'space-around',
    },
    skeletonDescription: {
      extend: 'description',
      minHeight: 32,
      maxWidth: 800,
    },
    skeletonDotsItem: {
      borderRadius: CornerRadius.circle,
      width: 8,
      height: 8,
    },
  }),
  {
    name: 'more-tools-to-explore',
  },
);

export default useStyles;
